import { useAction, useMutation, useQuery } from "convex/react";
import {
  ArrowLeft,
  Archive,
  CaretDown,
  ChatCircleDots,
  Check,
  CheckCircle,
  DotsThreeVertical,
  EnvelopeSimple,
  Eraser,
  MagnifyingGlass,
  Paperclip,
  PaperPlaneTilt,
  Plus,
  Smiley,
  ShareNetwork,
  Star,
  ThreadsLogo,
  TiktokLogo,
  TelegramLogo,
  InstagramLogo,
  FacebookLogo,
  WhatsappLogo,
  Trash,
  UserCircle,
  UserMinus,
} from "@phosphor-icons/react";
import { useEffect, useMemo, useRef, useState, type FormEvent } from "react";

import { api } from "../../convex/_generated/api";
import type { Id } from "../../convex/_generated/dataModel";
import { navigateToAppPath } from "../navigation";
import {
  buildTimeline,
  canSendHumanReply,
  contactLabel,
  formatTime,
  messagePreview,
  relativeTime,
  canAdministerAssignments,
} from "./inboxUtils";
import type {
  BusinessMembership,
  ConversationMessage,
  ConversationSummary,
  CurrentMembership,
  InternalNote,
} from "./types";

type InboxFilter = "all" | "unread" | "human" | "handoff" | "closed";
type MobileView = "list" | "thread";

const filters: Array<{ key: InboxFilter; label: string }> = [
  { key: "all", label: "All" },
  { key: "unread", label: "Unread" },
  { key: "human", label: "Human" },
  { key: "handoff", label: "Handoff" },
  { key: "closed", label: "Closed" },
];

const inboxChannels = [
  { key: "whatsapp", label: "WhatsApp" },
  { key: "messenger", label: "Messenger" },
  { key: "instagram", label: "Instagram" },
  { key: "threads", label: "Threads" },
  { key: "tiktok", label: "TikTok" },
  { key: "telegram", label: "Telegram" },
] as const;

type InboxChannelKey = (typeof inboxChannels)[number]["key"];

const filterMatches: Record<
  InboxFilter,
  (conversation: ConversationSummary) => boolean
> = {
  all: () => true,
  unread: (conversation) => conversation.unreadMessageCount > 0,
  human: (conversation) => conversation.ownershipState === "HUMAN_OWNED",
  handoff: (conversation) =>
    conversation.ownershipState === "HANDOFF_REQUESTED",
  closed: (conversation) => conversation.state === "CLOSED",
};

const illustrativeConversationId =
  "illustrative-conversation" as Id<"conversations">;
const illustrativeContactId = "11724841436683213" as Id<"contacts">;
const illustrativeNow = Date.now();

function illustrativeConversation(
  businessId: Id<"businesses">,
): ConversationSummary {
  return {
    conversationId: illustrativeConversationId,
    businessId,
    contactId: illustrativeContactId,
    contact: {
      contactId: illustrativeContactId,
      canonicalWhatsAppIdentity: "6059207023",
      normalizedPhoneIdentity: "+27678888634",
      displayName: "Ntsikzin",
      channel: "Telegram",
    } as ConversationSummary["contact"] & { channel: string },
    state: "OPEN",
    lastActivityAt: illustrativeNow - 9 * 24 * 60 * 60 * 1000,
    unreadMessageCount: 0,
    messageCount: 8,
    createdAt: illustrativeNow - 9 * 24 * 60 * 60 * 1000,
    updatedAt: illustrativeNow - 9 * 24 * 60 * 60 * 1000,
    ownershipState: "AI_OWNED",
    systemResponseEligible: true,
    assignee: null,
    lastMessage: {
      direction: "INBOUND",
      messageType: "TEXT",
      text: "I’m what you call a bot",
      processedAt: illustrativeNow - 9 * 24 * 60 * 60 * 1000,
    },
  };
}

function illustrativeMessages(
  businessId: Id<"businesses">,
): ConversationMessage[] {
  const messages = [
    ["/start", "INBOUND"],
    ["Hello", "INBOUND"],
    ["How much to install gas geyser?", "INBOUND"],
    ["my number is 067 8888 634 give me a call back", "INBOUND"],
    ["Hello there", "OUTBOUND"],
    ["😊", "OUTBOUND"],
    ["are you still interested?", "OUTBOUND"],
    ["I’m what you call a bot", "INBOUND"],
  ] as const;

  return messages.map(([text, direction], index) => ({
    messageId: ("illustrative-message-" + index) as Id<"inboundMessages">,
    businessId,
    contactId: illustrativeContactId,
    conversationId: illustrativeConversationId,
    direction,
    messageType: "TEXT",
    text,
    unsupportedType: null,
    processedAt: illustrativeNow - (messages.length - index) * 60 * 60 * 1000,
  }));
}

function inferConversationLanguage(messages: ConversationMessage[]) {
  const firstInboundText = messages
    .filter(
      (message) =>
        message.direction === "INBOUND" &&
        Boolean(message.text?.trim()) &&
        !message.text?.trim().startsWith("/"),
    )
    .sort((a, b) => a.processedAt - b.processedAt)[0]?.text;

  if (!firstInboundText) return null;

  const words = new Set(
    firstInboundText
      .toLocaleLowerCase()
      .replace(/[^\p{L}\p{N}\s]/gu, " ")
      .split(/\s+/)
      .filter(Boolean),
  );
  const languageSignals = [
    { language: "English", words: ["hello", "how", "what", "my", "is"] },
    { language: "isiZulu", words: ["sawubona", "unjani", "ngiyabonga"] },
    { language: "Sesotho", words: ["dumela", "kea", "leboha"] },
    { language: "Afrikaans", words: ["hallo", "hoe", "wat", "my"] },
    { language: "Spanish", words: ["hola", "cómo", "que", "mi"] },
    { language: "French", words: ["bonjour", "comment", "ma"] },
  ];
  const match = languageSignals
    .map((signal) => ({
      ...signal,
      score: signal.words.filter((word) => words.has(word)).length,
    }))
    .sort((a, b) => b.score - a.score)[0];

  return match?.score ? match.language : null;
}

function initials(conversation: ConversationSummary) {
  return contactLabel(conversation)
    .split(/\s+/)
    .slice(0, 2)
    .map((part) => part[0])
    .join("")
    .toUpperCase();
}

function selectedConversationFor(
  conversations: ConversationSummary[],
  requestedId: Id<"conversations"> | null,
) {
  return (
    conversations.find(
      (conversation) => conversation.conversationId === requestedId,
    ) ?? null
  );
}

function conversationChannel(conversation: ConversationSummary) {
  const contact = conversation.contact as
    (ConversationSummary["contact"] & { channel?: string | null }) | null;
  return contact?.channel ?? "Messaging";
}

export function InboxApp({ business }: { business: BusinessMembership }) {
  const [activeFilter, setActiveFilter] = useState<InboxFilter>("all");
  const [mobileView, setMobileView] = useState<MobileView>("list");
  const [requestedConversationId, setRequestedConversationId] =
    useState<Id<"conversations"> | null>(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [createInboxOpen, setCreateInboxOpen] = useState(false);
  const businessId = business.businessId;

  const conversationsQuery = useQuery(api.conversations.listForBusiness, {
    businessId,
  });
  const conversations = useMemo(() => {
    if (!conversationsQuery) return [];
    if (conversationsQuery.length > 0) {
      return conversationsQuery as ConversationSummary[];
    }
    return [illustrativeConversation(businessId)];
  }, [businessId, conversationsQuery]);
  const membership = useQuery(api.memberships.getForBusiness, {
    businessId,
  }) as CurrentMembership | undefined;
  const selectedConversation =
    selectedConversationFor(conversations, requestedConversationId) ??
    (conversations.length === 1 &&
    conversations[0].conversationId === illustrativeConversationId
      ? conversations[0]
      : null);
  const visibleConversations = useMemo(() => {
    const normalizedQuery = searchQuery.trim().toLowerCase();
    return conversations
      .filter(filterMatches[activeFilter])
      .filter((conversation) => {
        if (!normalizedQuery) return true;
        return [
          contactLabel(conversation),
          messagePreview(conversation),
          conversation.contact?.normalizedPhoneIdentity ?? "",
        ].some((value) => value.toLowerCase().includes(normalizedQuery));
      });
  }, [activeFilter, conversations, searchQuery]);

  useEffect(() => {
    document.title = "Inbox — Nkuli";
  }, []);

  function selectConversation(conversationId: Id<"conversations">) {
    setRequestedConversationId(conversationId);
    setMobileView("thread");
  }

  function backToList() {
    setMobileView("list");
  }

  return (
    <div
      className="inbox-canvas"
      data-mobile-view={selectedConversation ? mobileView : "list"}
    >
      <header className="inbox-canvas-header">
        <button
          aria-label="Connect a Channel"
          className="create-inbox-trigger"
          onClick={() => setCreateInboxOpen(true)}
          type="button"
        >
          <Plus aria-hidden="true" />
          <span>Connect a Channel</span>
        </button>
      </header>
      <div className="inbox-surface">
        <InboxList
          activeFilter={activeFilter}
          conversations={visibleConversations}
          counts={conversations}
          onFilterChange={setActiveFilter}
          onSearchChange={setSearchQuery}
          onSelect={selectConversation}
          searchQuery={searchQuery}
          selectedId={selectedConversation?.conversationId ?? null}
        />
        <ConversationPane
          business={business}
          conversation={selectedConversation}
          membership={membership ?? null}
          onBack={backToList}
        />
        <DetailsPane conversation={selectedConversation} />
      </div>
      {createInboxOpen ? (
        <CreateInboxModal
          onClose={() => setCreateInboxOpen(false)}
          onWhatsAppContinue={() =>
            navigateToAppPath("/app/whatsapp-connection")
          }
        />
      ) : null}
    </div>
  );
}

function channelIcon(channel: InboxChannelKey) {
  switch (channel) {
    case "whatsapp":
      return <WhatsappLogo weight="bold" />;
    case "messenger":
      return <FacebookLogo weight="bold" />;
    case "instagram":
      return <InstagramLogo weight="bold" />;
    case "threads":
      return <ThreadsLogo weight="bold" />;
    case "tiktok":
      return <TiktokLogo weight="bold" />;
    case "telegram":
      return <TelegramLogo weight="bold" />;
  }
}

function CreateInboxModal({
  onClose,
  onWhatsAppContinue,
}: {
  onClose: () => void;
  onWhatsAppContinue: () => void;
}) {
  return (
    <div className="create-inbox-backdrop" role="presentation">
      <section
        aria-label="Connect a channel"
        aria-modal="true"
        className="create-inbox-modal"
        role="dialog"
      >
        <header className="create-inbox-modal-header">
          <h2>Connect a channel</h2>
          <button
            aria-label="Close create inbox dialog"
            className="create-inbox-close"
            onClick={onClose}
            type="button"
          >
            ×
          </button>
        </header>
        <div className="create-inbox-channel-list">
          {inboxChannels.map((channel) => (
            <div className="create-inbox-channel" key={channel.key}>
              <div className={`create-inbox-channel-icon ${channel.key}`}>
                {channelIcon(channel.key)}
              </div>
              <span>{channel.label}</span>
              <button
                aria-label={`Connect ${channel.label}`}
                className="create-inbox-continue"
                disabled={channel.key !== "whatsapp"}
                onClick={
                  channel.key === "whatsapp" ? onWhatsAppContinue : undefined
                }
                type="button"
              >
                Connect
              </button>
            </div>
          ))}
        </div>
      </section>
    </div>
  );
}

function InboxList({
  activeFilter,
  conversations,
  counts,
  onFilterChange,
  onSearchChange,
  onSelect,
  searchQuery,
  selectedId,
}: {
  activeFilter: InboxFilter;
  conversations: ConversationSummary[];
  counts: ConversationSummary[];
  onFilterChange: (filter: InboxFilter) => void;
  onSearchChange: (value: string) => void;
  onSelect: (conversationId: Id<"conversations">) => void;
  searchQuery: string;
  selectedId: Id<"conversations"> | null;
}) {
  const [filterMenuOpen, setFilterMenuOpen] = useState(false);
  const activeFilterLabel =
    filters.find((filter) => filter.key === activeFilter)?.label ?? "All";

  return (
    <section className="inbox-list" aria-label="Inbox conversations">
      <div className="inbox-list-tools">
        <div className="inbox-filter-menu">
          <button
            aria-expanded={filterMenuOpen}
            aria-haspopup="menu"
            className="inbox-filter-trigger"
            onClick={() => setFilterMenuOpen((open) => !open)}
            type="button"
          >
            <span>{activeFilterLabel}</span>
            <CaretDown aria-hidden="true" />
          </button>
          {filterMenuOpen ? (
            <div className="inbox-filter-dropdown" role="menu">
              {filters.map((filter) => {
                const count = counts.filter(filterMatches[filter.key]).length;
                return (
                  <button
                    aria-pressed={activeFilter === filter.key}
                    className={activeFilter === filter.key ? "is-active" : ""}
                    key={filter.key}
                    onClick={() => {
                      onFilterChange(filter.key);
                      setFilterMenuOpen(false);
                    }}
                    role="menuitem"
                    type="button"
                  >
                    <span>{filter.label}</span>
                    {count > 0 ? <span>{count}</span> : null}
                  </button>
                );
              })}
            </div>
          ) : null}
        </div>
      </div>
      <label className="inbox-search-field">
        <MagnifyingGlass aria-hidden="true" />
        <span className="sr-only">Search conversations</span>
        <input
          aria-label="Search conversations"
          onChange={(event) => onSearchChange(event.target.value)}
          placeholder="Search conversations"
          type="search"
          value={searchQuery}
        />
      </label>
      <div className="conversation-list" role="list">
        {conversations.map((conversation) => (
          <button
            aria-current={
              selectedId === conversation.conversationId ? "true" : undefined
            }
            className="conversation-item"
            key={conversation.conversationId}
            onClick={() => onSelect(conversation.conversationId)}
            type="button"
          >
            <span
              className={`conversation-avatar${
                conversationChannel(conversation) === "Telegram"
                  ? " has-channel"
                  : ""
              }`}
            >
              {initials(conversation)}
              {conversationChannel(conversation) === "Telegram" ? (
                <span
                  aria-label="Telegram channel"
                  className="conversation-channel-badge"
                >
                  <TelegramLogo weight="fill" />
                </span>
              ) : null}
            </span>
            <span className="conversation-item-copy">
              <span className="conversation-item-topline">
                <strong>{contactLabel(conversation)}</strong>
                <time>{relativeTime(conversation.lastActivityAt)}</time>
              </span>
              <span className="conversation-item-preview">
                {messagePreview(conversation)}
              </span>
            </span>
            {conversation.unreadMessageCount > 0 ? (
              <span className="conversation-unread">
                {conversation.unreadMessageCount}
              </span>
            ) : null}
          </button>
        ))}
        {conversations.length === 0 ? (
          <div className="list-empty">
            <span>No conversations yet</span>
            <p>New customer messages will appear here.</p>
          </div>
        ) : null}
      </div>
    </section>
  );
}

function ConversationPane({
  business,
  conversation,
  membership,
  onBack,
}: {
  business: BusinessMembership;
  conversation: ConversationSummary | null;
  membership: CurrentMembership | null;
  onBack: () => void;
}) {
  return (
    <section className="conversation-pane" aria-label="Selected conversation">
      {conversation ? (
        <ActiveConversation
          business={business}
          conversation={conversation}
          membership={membership}
          onBack={onBack}
        />
      ) : (
        <div className="conversation-empty">
          <span className="empty-mark">+</span>
          <h2>Select a conversation</h2>
          <p>Choose a customer thread to view messages and reply.</p>
        </div>
      )}
    </section>
  );
}

// This component composes the conversation header, timeline, assignment flow, and composer.
// Its branching is UI-state composition rather than algorithmic control flow.
// eslint-disable-next-line complexity
function ActiveConversation({
  business,
  conversation,
  membership,
  onBack,
}: {
  business: BusinessMembership;
  conversation: ConversationSummary;
  membership: CurrentMembership | null;
  onBack: () => void;
}) {
  const [actionsOpen, setActionsOpen] = useState(false);
  const [assignmentOpen, setAssignmentOpen] = useState(false);
  const [assignmentSearch, setAssignmentSearch] = useState("");
  const [selectedAssignee, setSelectedAssignee] = useState<string | null>(
    conversation.assignee?.membershipId ?? null,
  );
  const markRead = useMutation(api.conversations.markRead);
  const assign = useMutation(api.conversations.assign);
  const releaseAssignment = useMutation(api.conversations.releaseAssignment);
  const assignableMembers = useQuery(
    api.memberships.listAssignableForBusiness,
    membership && canAdministerAssignments(membership.role)
      ? { businessId: business.businessId }
      : "skip",
  ) as Array<{ membershipId: Id<"memberships">; role: string }> | undefined;
  const isIllustrative =
    conversation.conversationId === illustrativeConversationId;
  const messagesQuery = useQuery(
    api.conversations.listMessages,
    isIllustrative
      ? "skip"
      : {
          businessId: business.businessId,
          conversationId: conversation.conversationId,
        },
  );
  const messages = useMemo(() => {
    if (messagesQuery?.length) return messagesQuery as ConversationMessage[];
    if (isIllustrative) {
      return illustrativeMessages(business.businessId);
    }
    return [] as ConversationMessage[];
  }, [business.businessId, isIllustrative, messagesQuery]);
  const notesQuery = useQuery(
    api.conversations.listInternalNotes,
    isIllustrative
      ? "skip"
      : {
          businessId: business.businessId,
          conversationId: conversation.conversationId,
        },
  );
  const notes = (notesQuery ?? []) as InternalNote[];

  useEffect(() => {
    if (!isIllustrative && conversation.unreadMessageCount > 0) {
      void markRead({
        businessId: business.businessId,
        conversationId: conversation.conversationId,
      });
    }
  }, [
    business.businessId,
    conversation.conversationId,
    conversation.unreadMessageCount,
    isIllustrative,
    markRead,
  ]);

  const timeline = buildTimeline(messages, notes);
  const filteredMembers = (assignableMembers ?? []).filter((member) =>
    `${member.role} ${member.membershipId}`
      .toLowerCase()
      .includes(assignmentSearch.trim().toLowerCase()),
  );

  async function confirmAssignment() {
    if (!selectedAssignee) {
      await releaseAssignment({
        businessId: business.businessId,
        conversationId: conversation.conversationId,
      });
    } else {
      await assign({
        businessId: business.businessId,
        conversationId: conversation.conversationId,
        assigneeMembershipId: selectedAssignee as Id<"memberships">,
      });
    }
    setAssignmentOpen(false);
  }

  return (
    <>
      <header className="conversation-header">
        <button
          aria-label="Back to conversations"
          className="mobile-back"
          onClick={onBack}
          type="button"
        >
          <ArrowLeft />
        </button>
        <div className="conversation-header-copy">
          <h2>{contactLabel(conversation)}</h2>
          <button
            aria-expanded={assignmentOpen}
            className="assignment-trigger"
            onClick={() => {
              setSelectedAssignee(conversation.assignee?.membershipId ?? null);
              setAssignmentOpen(true);
            }}
            type="button"
          >
            Assign conversation
            <CaretDown />
          </button>
        </div>
        <div className="conversation-header-actions">
          <button
            aria-expanded={actionsOpen}
            aria-haspopup="menu"
            aria-label="Conversation actions"
            onClick={() => setActionsOpen((open) => !open)}
            type="button"
          >
            <DotsThreeVertical />
          </button>
          {actionsOpen ? (
            <div className="conversation-actions-menu" role="menu">
              <button role="menuitem" type="button">
                <Star />
                Mark as Follow Up
              </button>
              <button role="menuitem" type="button">
                <EnvelopeSimple />
                Mark as Unread
              </button>
              <button role="menuitem" type="button">
                <Archive />
                Archive
              </button>
              <button role="menuitem" type="button">
                <UserMinus />
                Block Contact
              </button>
              <button className="is-danger" role="menuitem" type="button">
                <Trash />
                Delete Contact
              </button>
            </div>
          ) : null}
        </div>
      </header>
      {assignmentOpen ? (
        <div className="assignment-backdrop" role="presentation">
          <section
            aria-label="Assign conversation"
            aria-modal="true"
            className="assignment-modal"
            role="dialog"
          >
            <header className="assignment-modal-header">
              <h3>Assign conversation</h3>
              <button
                aria-label="Close assignment dialog"
                onClick={() => setAssignmentOpen(false)}
                type="button"
              >
                ×
              </button>
            </header>
            <label
              className="assignment-field-label"
              htmlFor="assignment-search"
            >
              Assign to
            </label>
            <div className="assignment-search">
              <MagnifyingGlass />
              <input
                id="assignment-search"
                onChange={(event) => setAssignmentSearch(event.target.value)}
                placeholder="Search team members"
                value={assignmentSearch}
              />
            </div>
            <div className="assignment-options" role="listbox">
              {filteredMembers.length > 0 ? (
                filteredMembers.map((member, index) => (
                  <button
                    aria-selected={selectedAssignee === member.membershipId}
                    className={
                      selectedAssignee === member.membershipId
                        ? "is-selected"
                        : ""
                    }
                    key={member.membershipId}
                    onClick={() => setSelectedAssignee(member.membershipId)}
                    role="option"
                    type="button"
                  >
                    <span>
                      <strong>Team member {index + 1}</strong>
                      <small>{member.role}</small>
                    </span>
                    {selectedAssignee === member.membershipId ? (
                      <CheckCircle weight="fill" />
                    ) : null}
                  </button>
                ))
              ) : (
                <p className="assignment-empty">No team members found.</p>
              )}
            </div>
            <footer className="assignment-modal-actions">
              <button
                className="assignment-remove"
                onClick={() => setSelectedAssignee(null)}
                type="button"
              >
                Remove assignee
              </button>
              <button
                className="assignment-cancel"
                onClick={() => setAssignmentOpen(false)}
                type="button"
              >
                Cancel
              </button>
              <button
                className="assignment-confirm"
                disabled={!selectedAssignee && !conversation.assignee}
                onClick={() => void confirmAssignment()}
                type="button"
              >
                Confirm
              </button>
            </footer>
          </section>
        </div>
      ) : null}
      <div className="message-scroll">
        {timeline.length > 0 ? (
          <div className="message-thread">
            {timeline.map((item) =>
              item.kind === "message" ? (
                <MessageBubble
                  key={`message-${item.message.messageId}`}
                  message={item.message}
                />
              ) : (
                <div className="internal-note" key={`note-${item.note.noteId}`}>
                  <strong>Internal note</strong>
                  <p>{item.note.body}</p>
                </div>
              ),
            )}
          </div>
        ) : (
          <div className="thread-empty">
            No messages in this conversation yet.
          </div>
        )}
      </div>
      <ReplyComposer
        business={business}
        conversation={conversation}
        membership={membership}
      />
    </>
  );
}

function MessageBubble({ message }: { message: ConversationMessage }) {
  const outbound = message.direction === "OUTBOUND";
  return (
    <article
      className={`message-bubble ${outbound ? "is-outbound" : "is-inbound"}`}
    >
      <p>
        {message.text ?? `Unsupported ${message.unsupportedType ?? "message"}`}
      </p>
      <time>{formatTime(message.processedAt)}</time>
      {outbound && message.deliveryStatus ? <Check weight="bold" /> : null}
    </article>
  );
}

// This component composes the flow, emoji, saved-reply, attachment, and send states.
// eslint-disable-next-line complexity
function ReplyComposer({
  business,
  conversation,
  membership,
}: {
  business: BusinessMembership;
  conversation: ConversationSummary;
  membership: CurrentMembership | null;
}) {
  const sendText = useAction(api.outboundSend.sendText);
  const [text, setText] = useState("");
  const [sending, setSending] = useState(false);
  const [sendError, setSendError] = useState(false);
  const [openPicker, setOpenPicker] = useState<
    "flow" | "emoji" | "reply" | null
  >(null);
  const [flowTab, setFlowTab] = useState<"flows" | "node">("flows");
  const [selectedFlow, setSelectedFlow] = useState("");
  const [attachmentName, setAttachmentName] = useState<string | null>(null);
  const attachmentInputRef = useRef<HTMLInputElement>(null);
  const allowed = canSendHumanReply(conversation, membership);
  const canDraft =
    conversation.state !== "CLOSED" && conversation.state !== "ARCHIVED";
  const contact = conversation.contact as ContactProfile | null | undefined;
  const channel = contact?.channel ?? "Messaging";

  function insertText(value: string) {
    setText((current) => (current ? `${current} ${value}` : value));
    setOpenPicker(null);
  }

  async function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!text.trim() || sending || !allowed) return;
    setSending(true);
    setSendError(false);
    try {
      await sendText({
        businessId: business.businessId,
        conversationId: conversation.conversationId,
        text: text.trim(),
        clientRequestId: crypto.randomUUID(),
        messageType: "TEXT",
      });
      setText("");
    } catch {
      setSendError(true);
    } finally {
      setSending(false);
    }
  }

  return (
    <form className="message-composer" onSubmit={(event) => void submit(event)}>
      <textarea
        aria-label="Message composer"
        disabled={!canDraft || sending}
        onChange={(event) => setText(event.target.value)}
        placeholder="Type a message"
        rows={3}
        value={text}
      />
      {sendError ? (
        <p className="message-composer-error" role="alert">
          We couldn&apos;t send that message. Try again.
        </p>
      ) : null}
      {openPicker === "flow" ? (
        <div className="flow-modal-backdrop" role="presentation">
          <section
            aria-label="Insert Flow"
            aria-modal="true"
            className="flow-modal"
            role="dialog"
          >
            <header className="flow-modal-header">
              <h3>Insert Flow</h3>
              <button
                aria-label="Close insert flow dialog"
                onClick={() => setOpenPicker(null)}
                type="button"
              >
                ×
              </button>
            </header>
            <div className="flow-tabs" role="tablist">
              <button
                aria-selected={flowTab === "flows"}
                onClick={() => setFlowTab("flows")}
                role="tab"
                type="button"
              >
                Flows
              </button>
              <button
                aria-selected={flowTab === "node"}
                onClick={() => setFlowTab("node")}
                role="tab"
                type="button"
              >
                Node
              </button>
            </div>
            {flowTab === "flows" ? (
              <label className="flow-select-label" htmlFor="flow-select">
                <span>Select flow</span>
                <select
                  id="flow-select"
                  onChange={(event) => setSelectedFlow(event.target.value)}
                  value={selectedFlow}
                >
                  <option value="">Select flow</option>
                  <option value="Qualify this lead">Qualify this lead</option>
                  <option value="Book an appointment">
                    Book an appointment
                  </option>
                  <option value="Request a human handoff">
                    Request a human handoff
                  </option>
                </select>
              </label>
            ) : (
              <p className="flow-node-empty">
                Select a flow first to choose its starting node.
              </p>
            )}
            <footer className="flow-modal-actions">
              <button
                className="flow-cancel"
                onClick={() => setOpenPicker(null)}
                type="button"
              >
                Cancel
              </button>
              <button
                className="flow-send"
                disabled={!selectedFlow || flowTab !== "flows"}
                onClick={() => {
                  insertText(`/flow ${selectedFlow.toLowerCase()}`);
                  setSelectedFlow("");
                }}
                type="button"
              >
                Insert
              </button>
            </footer>
          </section>
        </div>
      ) : null}
      {openPicker && openPicker !== "flow" ? (
        <div className="composer-picker" role="menu">
          {openPicker === "emoji" ? (
            <>
              <strong>Emoji</strong>
              <div className="emoji-picker-grid">
                {["😊", "🙂", "👍", "❤️", "🎉", "🙏", "😂", "🔥"].map(
                  (emoji) => (
                    <button
                      key={emoji}
                      aria-label={`Insert ${emoji}`}
                      onClick={() => insertText(emoji)}
                      type="button"
                    >
                      {emoji}
                    </button>
                  ),
                )}
              </div>
            </>
          ) : null}
          {openPicker === "reply" ? (
            <>
              <strong>Saved replies</strong>
              {[
                "Thanks for reaching out. How can we help?",
                "We have received your request and will get back to you shortly.",
                "Would you like us to arrange a call back?",
              ].map((reply) => (
                <button
                  key={reply}
                  onClick={() => insertText(reply)}
                  role="menuitem"
                  type="button"
                >
                  {reply}
                </button>
              ))}
            </>
          ) : null}
        </div>
      ) : null}
      <div className="message-composer-toolbar">
        <div
          className="message-composer-channel"
          aria-label={`Channel: ${channel}`}
        >
          <span className="message-composer-channel-icon">
            <TelegramLogo weight="fill" />
          </span>
          <strong>{channel}</strong>
        </div>
        <div className="message-composer-actions">
          <button
            aria-expanded={openPicker === "flow"}
            aria-label="Choose flow"
            onClick={() => setOpenPicker(openPicker === "flow" ? null : "flow")}
            type="button"
          >
            <ShareNetwork />
          </button>
          <button
            aria-expanded={openPicker === "emoji"}
            aria-label="Add emoji"
            onClick={() =>
              setOpenPicker(openPicker === "emoji" ? null : "emoji")
            }
            type="button"
          >
            <Smiley />
          </button>
          <button
            aria-expanded={openPicker === "reply"}
            aria-label="Saved replies"
            onClick={() =>
              setOpenPicker(openPicker === "reply" ? null : "reply")
            }
            type="button"
          >
            <ChatCircleDots />
          </button>
          <button
            aria-label="Attach document"
            onClick={() => attachmentInputRef.current?.click()}
            type="button"
          >
            <Paperclip />
          </button>
          <input
            accept=".csv,.doc,.docx,.pdf,.txt,.xls,.xlsx"
            aria-label="Document attachment"
            hidden
            onChange={(event) =>
              setAttachmentName(event.target.files?.[0]?.name ?? null)
            }
            ref={attachmentInputRef}
            type="file"
          />
          <button
            aria-label="Send message"
            className="message-composer-send"
            disabled={!allowed || !text.trim() || sending}
            type="submit"
          >
            <PaperPlaneTilt weight="fill" />
          </button>
        </div>
      </div>
      {attachmentName ? (
        <span className="composer-attachment">{attachmentName}</span>
      ) : null}
    </form>
  );
}

type ContactProfile = NonNullable<ConversationSummary["contact"]> & {
  channel?: string | null;
  email?: string | null;
  firstName?: string | null;
  gender?: string | null;
  language?: string | null;
  lastName?: string | null;
};

// This component composes the customer summary and optional detail sections.
// eslint-disable-next-line complexity
function DetailsPane({
  conversation,
}: {
  conversation: ConversationSummary | null;
}) {
  const isIllustrative =
    conversation?.conversationId === illustrativeConversationId;
  const messagesQuery = useQuery(
    api.conversations.listMessages,
    conversation && !isIllustrative
      ? {
          businessId: conversation.businessId,
          conversationId: conversation.conversationId,
        }
      : "skip",
  );
  const messages = useMemo(() => {
    if (messagesQuery?.length) return messagesQuery as ConversationMessage[];
    if (conversation && isIllustrative) {
      return illustrativeMessages(conversation.businessId);
    }
    return [] as ConversationMessage[];
  }, [conversation, isIllustrative, messagesQuery]);
  const name = conversation ? contactLabel(conversation) : "Customer details";
  const contact = conversation?.contact as ContactProfile | null | undefined;
  const firstName = contact?.firstName ?? name.split(" ")[0] ?? "Not available";
  const lastName = contact?.lastName ?? "Not available";
  const identity =
    contact?.normalizedPhoneIdentity ??
    contact?.canonicalWhatsAppIdentity ??
    "Not available";
  const language =
    contact?.language ?? inferConversationLanguage(messages) ?? "Not available";

  return (
    <aside className="details-pane" aria-label="Customer details">
      {conversation ? (
        <>
          <div className="details-avatar">{initials(conversation)}</div>
          <h2>{name}</h2>
          <p className="details-subtitle">
            {contact?.channel ?? "Messaging contact"}
          </p>
          <dl className="details-list">
            <div>
              <dt>Contact ID</dt>
              <dd>{conversation.contactId}</dd>
            </div>
            <div>
              <dt>Identity</dt>
              <dd>{identity}</dd>
            </div>
            <div>
              <dt>Language</dt>
              <dd>{language}</dd>
            </div>
            <div>
              <dt>Gender</dt>
              <dd>{contact?.gender ?? "Not available"}</dd>
            </div>
            <div>
              <dt>Email</dt>
              <dd>{contact?.email ?? "Not available"}</dd>
            </div>
            <div>
              <dt>First name</dt>
              <dd>{firstName}</dd>
            </div>
            <div>
              <dt>Last name</dt>
              <dd>{lastName}</dd>
            </div>
            <div>
              <dt>Phone number</dt>
              <dd>{contact?.normalizedPhoneIdentity ?? "Not available"}</dd>
            </div>
            <div>
              <dt>Messages</dt>
              <dd>{conversation.messageCount}</dd>
            </div>
            <div>
              <dt>Last active</dt>
              <dd>{relativeTime(conversation.lastActivityAt)}</dd>
            </div>
          </dl>
          <div className="details-field-actions">
            <button type="button">
              <Plus />
              Add custom field
            </button>
            <button className="is-clear" type="button">
              <Eraser />
              Clear
            </button>
          </div>
          <div className="details-section-heading">
            <span>Notes (0)</span>
            <button aria-label="Add note" type="button">
              <Plus />
            </button>
          </div>
          <p className="details-empty">No notes added yet.</p>
          <div className="details-accordions">
            {["Coupons", "Appointments", "Tags"].map((label) => (
              <details key={label}>
                <summary>
                  <span>{label}</span>
                  <CaretDown aria-hidden="true" />
                </summary>
                <p>Nothing added yet.</p>
              </details>
            ))}
          </div>
        </>
      ) : (
        <div className="details-empty-state">
          <UserCircle />
          <h2>{name}</h2>
          <p>Select a conversation to see customer context.</p>
        </div>
      )}
    </aside>
  );
}
